import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getNcdRiskAnalysis($filter: ncdRiskAnalysisFilterInput!) {
    getNcdRiskAnalysis(filter: $filter) {
      totalReadingCount
      riskCount
      zeroRiskCount
      oneRiskCount
      twoRiskCount
      threeRiskCount
      fourRiskCount
      fiveRiskCount
      hypertensionRiskCount
      noHypertensionRiskCount
      hypotensionRiskCount
      noHypotensionRiskCount
      noHyperHypoTensionRiskCount
      diabeticRiskCount
      noDiabeticRiskCount
      cardiacRiskCount
      noCardiacRiskCount
      obesityRiskCount
      noObesityRiskCount
      respiratoryRiskCount
      noRespiratoryRiskCount
      noCovidRiskCount
      mildCovidRiskCount
      moderateCovidRiskCount
      severeCovidRiskCount
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.NcdRiskAnalysisState = {
  result: null,
  error: null,
  loading: false
};

export const getNcdRiskAnalysisRequest = createAsyncThunk<
  TYPES.NcdRiskAnalysis,
  {filter: TYPES.NcdRiskAnalysisFilterInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'reading/get_ncd_risk_analysis',
  async (
  params: {filter: TYPES.NcdRiskAnalysisFilterInput;},
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getNcdRiskAnalysis;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getNcdRiskAnalysisSlice = createSlice({
  name: 'get_ncd_risk_analysis',
  initialState: initialState,
  reducers: {
    getNcdRiskAnalysisClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getNcdRiskAnalysisRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getNcdRiskAnalysisRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getNcdRiskAnalysisRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getNcdRiskAnalysisClear } =
getNcdRiskAnalysisSlice.actions;

export const getNcdRiskAnalysis = createSelector(
  (state: RootState) => state.reading.getNcdRiskAnalysis.result,
  (state: RootState) => state.reading.getNcdRiskAnalysis.error,
  (state: RootState) => state.reading.getNcdRiskAnalysis.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getNcdRiskAnalysisSlice.reducer;